export type ParallelRuntimeReleaseCertificationStatus = "qualification_pending" | "certified" | "revoked";

export interface ParallelRuntimeCertifiedProfile {
  id: string;
  platform: "android" | "ios" | "desktop" | "any";
  browserFamily: "chrome" | "safari" | "firefox" | "edge" | "any";
  minLogicalCpuCount: number;
  minDeviceMemoryGb: number | null;
  evidenceRelease: string;
  evidenceContract: string;
}

export interface ParallelRuntimeReleaseCertificationDecision {
  schemaVersion: 1;
  contractVersion: "browser-parallel-runtime-release-certification-v1";
  release: string;
  status: ParallelRuntimeReleaseCertificationStatus;
  matched: boolean;
  profileId: string | null;
  reason: string;
  perUserSoakRequired: false;
  certifiedProfiles: number;
}

// PV-11 keeps release certification as QA/advisory evidence only. Runtime
// activation no longer waits for a local 10/10 history or a frozen profile.
// A capable browser is admitted per session only after the real two-worker
// overlap/checksum probe and warm-pool checks pass; serial same-scan fallback
// remains mandatory. Certified profiles may still document validated fleets.
const RELEASE_CERTIFICATION = Object.freeze({
  schemaVersion: 1 as const,
  contractVersion: "browser-parallel-runtime-release-certification-v1" as const,
  release: "0.277.0",
  status: "qualification_pending" as ParallelRuntimeReleaseCertificationStatus,
  profiles: [] as ParallelRuntimeCertifiedProfile[],
});

function browserFamily(userAgent: string): ParallelRuntimeCertifiedProfile["browserFamily"] {
  const ua = userAgent.toLowerCase();
  if (ua.includes("edg/")) return "edge";
  if (ua.includes("firefox/")) return "firefox";
  if ((ua.includes("chrome/") || ua.includes("crios/")) && !ua.includes("edg/")) return "chrome";
  if (ua.includes("safari/") && !ua.includes("chrome/") && !ua.includes("crios/")) return "safari";
  return "any";
}

function platform(userAgent: string): ParallelRuntimeCertifiedProfile["platform"] {
  const ua = userAgent.toLowerCase();
  if (ua.includes("android")) return "android";
  if (/(iphone|ipad|ipod)/.test(ua)) return "ios";
  return "desktop";
}

export function evaluateParallelRuntimeReleaseCertification(): ParallelRuntimeReleaseCertificationDecision {
  if (typeof navigator === "undefined") {
    return {
      ...RELEASE_CERTIFICATION,
      matched: false,
      profileId: null,
      reason: "NO_BROWSER_RUNTIME",
      perUserSoakRequired: false,
      certifiedProfiles: RELEASE_CERTIFICATION.profiles.length,
    };
  }
  if (RELEASE_CERTIFICATION.status !== "certified" || RELEASE_CERTIFICATION.profiles.length === 0) {
    return {
      schemaVersion: 1,
      contractVersion: RELEASE_CERTIFICATION.contractVersion,
      release: RELEASE_CERTIFICATION.release,
      status: RELEASE_CERTIFICATION.status,
      matched: false,
      profileId: null,
      reason: "RELEASE_QUALIFICATION_PENDING",
      perUserSoakRequired: false,
      certifiedProfiles: RELEASE_CERTIFICATION.profiles.length,
    };
  }
  const ua = navigator.userAgent || "";
  const runtimePlatform = platform(ua);
  const runtimeBrowser = browserFamily(ua);
  const logicalCpuCount = Math.max(1, Number(navigator.hardwareConcurrency || 1));
  const memoryRaw = Number((navigator as Navigator & { deviceMemory?: number }).deviceMemory);
  const deviceMemoryGb = Number.isFinite(memoryRaw) && memoryRaw > 0 ? memoryRaw : null;
  const profile = RELEASE_CERTIFICATION.profiles.find((candidate) => {
    if (candidate.platform !== "any" && candidate.platform !== runtimePlatform) return false;
    if (candidate.browserFamily !== "any" && candidate.browserFamily !== runtimeBrowser) return false;
    if (logicalCpuCount < candidate.minLogicalCpuCount) return false;
    if (candidate.minDeviceMemoryGb !== null && (deviceMemoryGb === null || deviceMemoryGb < candidate.minDeviceMemoryGb)) return false;
    return true;
  });
  return {
    schemaVersion: 1,
    contractVersion: RELEASE_CERTIFICATION.contractVersion,
    release: RELEASE_CERTIFICATION.release,
    status: RELEASE_CERTIFICATION.status,
    matched: Boolean(profile),
    profileId: profile?.id ?? null,
    reason: profile ? "RELEASE_PROFILE_CERTIFIED" : "NO_CERTIFIED_PROFILE_MATCH",
    perUserSoakRequired: false,
    certifiedProfiles: RELEASE_CERTIFICATION.profiles.length,
  };
}
